import React, { useState, useEffect } from 'react';
import { 
  Bell, MessageCircle, Star, Heart, Share2, AlertTriangle, Check, X, 
  ChevronRight, ArrowLeft, Filter, Loader2, CheckCheck, Trash2
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
// Use centralized API service
import { alertsApi, notificationsApi } from '../services/api';

// Mapeamento de ícones por tipo
const NOTIFICATION_ICONS = {
  new_comment: MessageCircle,
  new_review: Star,
  new_like: Heart,
  new_favorite: Heart,
  new_share: Share2,
  new_news: Bell,
  new_evento: Bell,
  new_acao: Bell,
  new_capacitacao: Bell,
  new_financeiro: Bell,
  new_parceria: Bell,
  comment_reply: MessageCircle,
  comment_moderation: AlertTriangle,
  report_received: AlertTriangle,
  report_resolved: Check,
  content_reported: AlertTriangle,
  content_removed: X,
  account_warning: AlertTriangle,
  account_suspended: X,
  business_approved: Check,
  business_rejected: X,
  business_suspended: AlertTriangle
};

// Cores por tipo
const NOTIFICATION_COLORS = {
  new_comment: 'bg-blue-100 text-blue-600',
  new_review: 'bg-yellow-100 text-yellow-600',
  new_like: 'bg-pink-100 text-pink-600',
  new_favorite: 'bg-pink-100 text-pink-600',
  new_share: 'bg-purple-100 text-purple-600',
  new_news: 'bg-indigo-100 text-indigo-600',
  new_evento: 'bg-purple-100 text-purple-600',
  new_acao: 'bg-teal-100 text-teal-600',
  new_capacitacao: 'bg-blue-100 text-blue-600',
  new_financeiro: 'bg-green-100 text-green-600',
  new_parceria: 'bg-orange-100 text-orange-600',
  comment_reply: 'bg-blue-100 text-blue-600',
  comment_moderation: 'bg-orange-100 text-orange-600',
  report_received: 'bg-orange-100 text-orange-600',
  report_resolved: 'bg-green-100 text-green-600',
  content_reported: 'bg-orange-100 text-orange-600',
  content_removed: 'bg-red-100 text-red-600',
  account_warning: 'bg-yellow-100 text-yellow-600',
  account_suspended: 'bg-red-100 text-red-600',
  business_approved: 'bg-green-100 text-green-600',
  business_rejected: 'bg-red-100 text-red-600',
  business_suspended: 'bg-orange-100 text-orange-600'
};

// Labels dos tipos
const NOTIFICATION_LABELS = {
  new_comment: 'Comentário',
  new_review: 'Avaliação',
  new_like: 'Curtida',
  new_favorite: 'Favorito',
  new_share: 'Compartilhamento',
  new_news: 'Notícia',
  new_evento: 'Evento',
  new_acao: 'Ação',
  new_capacitacao: 'Capacitação',
  new_financeiro: 'Programa Financeiro',
  new_parceria: 'Parceria',
  comment_reply: 'Resposta',
  comment_moderation: 'Moderação',
  report_received: 'Denúncia',
  report_resolved: 'Denúncia resolvida',
  content_reported: 'Conteúdo denunciado',
  content_removed: 'Conteúdo removido',
  account_warning: 'Advertência',
  account_suspended: 'Conta suspensa',
  business_approved: 'Negócio aprovado',
  business_rejected: 'Negócio rejeitado',
  business_suspended: 'Negócio suspenso'
};

// Tempo relativo
const timeAgo = (dateStr) => {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = (now - d) / 1000;
  
  if (diff < 60) return 'agora';
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} dias`;
  return d.toLocaleDateString('pt-BR');
};

export default function MeusAlertas() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, unread: 0 });
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [typeFilter, setTypeFilter] = useState('all');
  const [processing, setProcessing] = useState(false);

  // Buscar notificações
  const fetchNotifications = async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const data = await notificationsApi.list({ limit: 100 });
      
      setNotifications(data.notifications || data || []);
      setStats({
        total: data.total || (data.notifications || data).length || 0,
        unread: data.unread_count || 0
      });
    } catch (err) {
      console.error('Erro ao carregar notificações:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      navigate('/perfil');
      return;
    }
    fetchNotifications();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  // Marcar como lida
  const markAsRead = async (notificationId) => {
    try {
      await notificationsApi.markRead(notificationId);
      
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      setStats(prev => ({ ...prev, unread: Math.max(0, prev.unread - 1) }));
    } catch (err) {
      console.error('Erro ao marcar como lida:', err);
    }
  };

  // Marcar todas como lidas
  const markAllAsRead = async () => {
    setProcessing(true);
    try {
      await notificationsApi.markAllRead();
      
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setStats(prev => ({ ...prev, unread: 0 }));
    } catch (err) {
      console.error('Erro ao marcar todas como lidas:', err);
    } finally {
      setProcessing(false);
    }
  };

  // Deletar notificação
  const deleteNotification = async (notificationId) => {
    try {
      await notificationsApi.delete(notificationId);
      
      const notif = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setStats(prev => ({
        total: prev.total - 1,
        unread: notif && !notif.is_read ? prev.unread - 1 : prev.unread
      }));
    } catch (err) {
      console.error('Erro ao deletar notificação:', err);
    }
  };

  // Filtrar notificações
  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread' && n.is_read) return false;
    if (filter === 'read' && !n.is_read) return false;
    if (typeFilter !== 'all' && n.type !== typeFilter) return false;
    return true;
  });

  // Tipos únicos para filtro
  const uniqueTypes = [...new Set(notifications.map(n => n.type))];

  // Navegar para o item relacionado
  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    
    if (notification.entity_type === 'business' && notification.entity_id) {
      navigate(`/negocio/${notification.entity_id}`);
    } else if (notification.entity_type === 'comment' && notification.entity_id) {
      // Navegar para o negócio com âncora de comentários
      navigate(`/negocio/${notification.entity_id}#comentarios`);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
                Meus Alertas
              </h1>
              <p className="text-sm text-gray-500">
                {stats.total} notificações • {stats.unread} não lidas
              </p>
            </div>
            {stats.unread > 0 && (
              <button
                onClick={markAllAsRead}
                disabled={processing}
                className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--primary)] hover:bg-[var(--primary)]/10 rounded-lg"
              >
                {processing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCheck className="w-4 h-4" />
                )}
                Marcar todas como lidas
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="max-w-3xl mx-auto px-4 py-4">
        <div className="flex flex-wrap gap-2 mb-4">
          {/* Filtro de status */}
          <div className="flex bg-white rounded-lg border p-1">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                filter === 'all' ? 'bg-[var(--primary)] text-white' : 'hover:bg-gray-100'
              }`}
            >
              Todas
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                filter === 'unread' ? 'bg-[var(--primary)] text-white' : 'hover:bg-gray-100'
              }`}
            >
              Não lidas ({stats.unread})
            </button>
            <button
              onClick={() => setFilter('read')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                filter === 'read' ? 'bg-[var(--primary)] text-white' : 'hover:bg-gray-100'
              }`}
            >
              Lidas
            </button>
          </div>

          {/* Filtro por tipo */}
          {uniqueTypes.length > 1 && (
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2 text-sm bg-white border rounded-lg"
            >
              <option value="all">Todos os tipos</option>
              {uniqueTypes.map(type => (
                <option key={type} value={type}>
                  {NOTIFICATION_LABELS[type] || type}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Lista de notificações */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <Bell className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              {filter === 'unread' ? 'Nenhuma notificação não lida' : 'Nenhuma notificação'}
            </h3>
            <p className="text-gray-500">
              {filter === 'unread' 
                ? 'Você já leu todas as suas notificações!'
                : 'Suas notificações aparecerão aqui'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredNotifications.map(notification => {
              const Icon = NOTIFICATION_ICONS[notification.type] || Bell;
              const colorClass = NOTIFICATION_COLORS[notification.type] || 'bg-gray-100 text-gray-600';
              
              return (
                <div
                  key={notification.id}
                  className={`bg-white rounded-xl border transition-all hover:shadow-md ${
                    !notification.is_read ? 'border-[var(--primary)]/30 bg-[var(--primary)]/5' : 'border-gray-100'
                  }`}
                >
                  <div 
                    className="p-4 cursor-pointer"
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start gap-3">
                      {/* Indicador de não lida */}
                      <div className="flex-shrink-0 mt-1">
                        {!notification.is_read && (
                          <div className="w-2 h-2 rounded-full bg-[var(--primary)]" />
                        )}
                        {notification.is_read && <div className="w-2" />}
                      </div>

                      {/* Ícone */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                        <Icon className="w-5 h-5" />
                      </div>

                      {/* Conteúdo */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className={`font-semibold text-gray-900 ${!notification.is_read ? 'text-[var(--primary)]' : ''}`}>
                              {notification.title}
                            </p>
                            <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">
                              {notification.content}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <span className="text-xs text-gray-400">
                              {timeAgo(notification.created_at)}
                            </span>
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          </div>
                        </div>

                        {/* Badge de tipo */}
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${colorClass}`}>
                            {NOTIFICATION_LABELS[notification.type] || notification.type}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Ações */}
                  <div className="px-4 pb-3 flex items-center gap-2 border-t border-gray-50 pt-2">
                    {!notification.is_read && (
                      <button
                        onClick={(e) => { e.stopPropagation(); markAsRead(notification.id); }}
                        className="text-xs text-[var(--primary)] hover:underline flex items-center gap-1"
                      >
                        <Check className="w-3 h-3" />
                        Marcar como lida
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteNotification(notification.id); }}
                      className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1 ml-auto"
                    >
                      <Trash2 className="w-3 h-3" />
                      Excluir
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
